import apolloClient from 'api/apolloClient';
import {
  ADD_ADDITIONAL_DOCUMENT,
  APPLY_USER_ACTION,
  DISCARD_DOCUMENT,
  EDIT_DOCUMENT,
  EDIT_TEMPLATE,
  GET_DOCUMENTS,
  GET_DOWNLOAD_DOCUMENT_URL,
  REMOVE_UPLOAD,
  REQUEST_FOR_SIGNATURE,
  REQUEST_FOR_SIGNATURE_FOR_FILE,
  SEND_SIGN_FILE_REMINDER,
  SUBMIT_DOCUMENTS,
  UPDATE_SUBSCRIBER_REASON,
  UPLOAD_DOCUMENT,
  UPLOAD_RFQ_RESPONSE_DOCUMENT,
} from 'api/queries/documentsService';
import { Document, Recipient, UploadStep, UserAction } from 'types/documentsService';
import { File } from 'types/policyService';

export interface EditTemplateInput {
  documentId: string;
  templateLink: string;
  fileName: string;
}
interface EditDocumentInput {
  documentId: string;
  type: string;
  description: string;
}
interface AdditionalDocumentInput {
  entityId: string;
  type: string;
  templateLink: string;
  description: string;
  fileName: string;
}

interface UpdateSubReasonInput {
  sponsorId: string;
  subId: string;
  reason: string;
  groupId: string;
}

export async function getDocuments(
  entityId: string,
  uploadStep: string,
  affiliatedId: string | null,
): Promise<Array<Document>> {
  return (
    await apolloClient.query<{ getDocuments: Array<Document> }>({
      query: GET_DOCUMENTS,
      fetchPolicy: 'no-cache',
      variables: { entityId, uploadStep, affiliatedId },
    })
  ).data.getDocuments;
}

export async function uploadDocument(
  entityId: string,
  sponsorId: string,
  type: string,
  fileName: string,
): Promise<string> {
  return (
    await apolloClient.mutate<{ uploadDocument: string }>({
      mutation: UPLOAD_DOCUMENT,
      fetchPolicy: 'no-cache',
      variables: { entityId, sponsorId, type, fileName },
    })
  ).data!.uploadDocument;
}

export async function uploadRFQResponseDocument(
  entityId: string,
  type: string,
  fileName: string,
  description: string,
): Promise<string> {
  return (
    await apolloClient.mutate<{ uploadRFQResponse: string }>({
      mutation: UPLOAD_RFQ_RESPONSE_DOCUMENT,
      fetchPolicy: 'no-cache',
      variables: { entityId, type, description, fileName },
    })
  ).data!.uploadRFQResponse;
}

export async function removeUpload(documentId: string): Promise<void> {
  await apolloClient.mutate({
    mutation: REMOVE_UPLOAD,
    fetchPolicy: 'no-cache',
    variables: { documentId },
  });
}

export async function discardDocument(documentId: string): Promise<void> {
  await apolloClient.mutate({
    mutation: DISCARD_DOCUMENT,
    fetchPolicy: 'no-cache',
    variables: { documentId },
  });
}

export async function getDownloadDocumentURL(documentId: string): Promise<string> {
  return (
    await apolloClient.query<{ getDownloadDocumentURL: string }>({
      query: GET_DOWNLOAD_DOCUMENT_URL,
      fetchPolicy: 'no-cache',
      variables: { documentId },
    })
  ).data.getDownloadDocumentURL;
}

export async function addAdditionalDocument({
  entityId,
  type,
  templateLink,
  description,
  fileName,
}: AdditionalDocumentInput): Promise<string> {
  return (
    await apolloClient.mutate<{ addAdditionalDocument: string }>({
      mutation: ADD_ADDITIONAL_DOCUMENT,
      fetchPolicy: 'no-cache',
      variables: { entityId, type, templateLink, description, fileName },
    })
  ).data!.addAdditionalDocument;
}

export async function editDocument(documentInput: EditDocumentInput): Promise<void> {
  await apolloClient.mutate({
    mutation: EDIT_DOCUMENT,
    fetchPolicy: 'no-cache',
    variables: { ...documentInput },
  });
}
export async function editTemplate(editTemplateInput: EditTemplateInput): Promise<string> {
  return (
    await apolloClient.mutate<{ editTemplate: string }>({
      mutation: EDIT_TEMPLATE,
      fetchPolicy: 'no-cache',
      variables: { ...editTemplateInput },
    })
  ).data!.editTemplate;
}

export async function applyUserAction(documentId: string, userAction: UserAction): Promise<void> {
  await apolloClient.mutate({
    mutation: APPLY_USER_ACTION,
    fetchPolicy: 'no-cache',
    variables: { documentId, userAction },
  });
}

export async function requestForSignature(documentId: string, recipient: Recipient): Promise<void> {
  await apolloClient.mutate({
    mutation: REQUEST_FOR_SIGNATURE,
    fetchPolicy: 'no-cache',
    variables: { documentId, recipient },
  });
}

export async function sendSignFileReminder(documentId: string): Promise<void> {
  await apolloClient.mutate({
    mutation: SEND_SIGN_FILE_REMINDER,
    fetchPolicy: 'no-cache',
    variables: { documentId },
  });
}

export async function requestForSignatureForFile(periodId: string, recipient: Recipient, file: File): Promise<void> {
  await apolloClient.mutate({
    mutation: REQUEST_FOR_SIGNATURE_FOR_FILE,
    fetchPolicy: 'no-cache',
    variables: { periodId, recipient, file },
  });
}

export async function updateSubscriberReason({
  subId,
  groupId,
  sponsorId,
  reason,
}: UpdateSubReasonInput): Promise<void> {
  await apolloClient.mutate({
    mutation: UPDATE_SUBSCRIBER_REASON,
    variables: { entityId: subId, affiliatedId: groupId, sponsorId, reason },
  });
}

export async function submitDocuments(entityId: string, uploadStep: UploadStep): Promise<void> {
  await apolloClient.mutate({
    mutation: SUBMIT_DOCUMENTS,
    variables: { entityId, uploadStep },
  });
}
